import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaApple } from 'react-icons/fa';
import { LuAtSign, LuEye, LuEyeOff, LuLock, LuMail, LuUser, LuUserPlus } from 'react-icons/lu';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  AuthAlert,
  AuthCheck,
  AuthDivider,
  AuthField,
  AuthLegalLink,
  AuthShell,
  GoogleMark,
} from '../components/auth/AuthShell';
import PendingJoinNote from '../components/circles/PendingJoinNote';
import {
  type PendingCircleJoin,
  parsePendingJoinParams,
  readPendingJoin,
  savePendingJoin,
} from '../components/circles/pendingCircleJoin';
import { Button } from '../design-system/components';
import { getStashedReferralCode, resolveReferralCode } from '../services/referral.service';
import { useAuthStore } from '../stores/auth.store';
import { fireAndForget } from '../utils/fireAndForget';
import { buildOAuthState } from '../utils/oauthState';
import { googleOAuthStartUrl } from '../services/google-app-nonce';
import { signedInTarget, withRedirect } from '../utils/safe-redirect';
import { getStashedSignupSource, getStashedSignupVariant } from '../utils/signup-source';

interface RegisterFormData {
  name: string;
  username: string;
  email: string;
  password: string;
  data_collection_consent?: boolean;
  terms_accepted: boolean;
  /** Honeypot — hidden from humans, so a value here means a script. */
  intakeRef?: string;
}

function Register() {
  // Navigation hook
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);

  // Sign in with Apple is shown only on iOS (Apple Review Guideline 4.8). Web +
  // Android already offer Google login and are not subject to the requirement.
  // See docs/plans/SIGN-IN-WITH-APPLE.md.
  const isApplePlatform = Capacitor.getPlatform() === 'ios';
  // React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = useForm<RegisterFormData>({ mode: 'all' });
  const [registerError, setRegisterError] = useState('');

  // When the form became fillable, so we can report how long it took to submit.
  // Analytics only — the server treats this as untrusted (a bot can claim any
  // number), so it is never a gate. See docs/plans/SIGNUP-INTEGRITY-2.md.
  const formShownAt = useRef(Date.now());

  // Auth store actions and state
  const userRegister = useAuthStore((state) => state.register);
  const startGuest = useAuthStore((state) => state.startGuest);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isGoogleLoading = useAuthStore((state) => state.isGoogleLoading);
  const setGoogleLoading = useAuthStore((state) => state.setGoogleLoading);
  const signInWithApple = useAuthStore((state) => state.signInWithApple);

  // Watch form fields to clear error on change
  const formValues = watch();

  // Clear register error when any field is changed.
  // Why: we only want to fire on user input changes; including registerError would
  // re-fire immediately after clearing it (no-op but unnecessary).
  useEffect(() => {
    if (registerError) setRegisterError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.name, formValues.username, formValues.email, formValues.password]);

  // Redirect if already authenticated
  const [searchParams] = useSearchParams();
  // True from this page's own sign-in until its post-auth steps are done.
  const postAuthPending = useRef(false);
  useEffect(() => {
    if (isAuthenticated) {
      // A sign-in this page started finishes its own post-auth steps first
      // and navigates when they are done (finishSignIn); PublicRoute leaves a
      // session that arrives while the page is open to the page (items 4/9).
      if (postAuthPending.current) return;
      void navigate(signedInTarget(searchParams.get('redirect')), { replace: true });
    }
  }, [isAuthenticated, navigate, searchParams]);

  /** After this page's own sign-in and its post-auth steps: follow the redirect. */
  const finishSignIn = () => {
    postAuthPending.current = false;
    if (useAuthStore.getState().isAuthenticated) {
      void navigate(signedInTarget(searchParams.get('redirect')), { replace: true });
    }
  };

  // A circle the visitor chose on the public notice (CIRCLE-STARTING-NOTICE.md
  // §5, S4): ?circle=<id> / ?hourly=<T>, validated and stored as numbers for
  // PendingCircleJoin, which runs it once they are in and verified. Never a URL.
  const circleParam = searchParams.get('circle');
  const [pendingJoin, setPendingJoin] = useState<PendingCircleJoin | null>(null);
  const hourlyParam = searchParams.get('hourly');
  useEffect(() => {
    const join = parsePendingJoinParams(
      new URLSearchParams([
        ...(circleParam !== null ? [['circle', circleParam]] : []),
        ...(hourlyParam !== null ? [['hourly', hourlyParam]] : []),
      ]),
    );
    if (join) savePendingJoin(join);
    // What is stored now (this link's, or a richer one the notice saved) is what
    // the runner will take them to: the note says so.
    setPendingJoin(readPendingJoin());
  }, [circleParam, hourlyParam]);

  // Referral capture: stash the invite code (?ref=CODE) so it rides along with the
  // signup (the auth store reads it from localStorage), and resolve the inviter's
  // first name for the "{Friend} invited you" banner. Persisted rather than passed
  // directly so it survives a bounce through /login or a page reload.
  const [inviterName, setInviterName] = useState<string | null>(null);
  // Depend on the primitive ?ref value, not the searchParams object, so the effect
  // re-runs only when the actual code changes (not on object-identity churn).
  const refParam = searchParams.get('ref');
  useEffect(() => {
    // Only accept a well-formed code (matches the server's format + max:16). Without
    // this, a crafted link like ?ref=<garbage> would be stashed and sent, and the
    // backend's max:16 validation would reject the WHOLE registration — a link anyone
    // could use to break signup. A malformed code is simply ignored.
    const isValidCode = (c: string | null): c is string => !!c && /^[A-Za-z0-9]{1,16}$/.test(c);

    const ref = refParam;
    if (isValidCode(ref)) {
      try {
        localStorage.setItem('referral_code', ref);
      } catch {
        /* Safari private mode */
      }
    }
    let code: string | null = ref;
    if (!isValidCode(code)) {
      try {
        code = localStorage.getItem('referral_code');
      } catch {
        /* ignore */
      }
    }
    if (isValidCode(code)) {
      // Never blocks the page — resolveReferralCode swallows its own errors.
      void resolveReferralCode(code).then(setInviterName);
    }
  }, [refParam]);

  const submitRegisterForm = async (postData: RegisterFormData) => {
    setIsLoading(true);
    postAuthPending.current = true;
    setRegisterError(''); // Clear any previous errors

    try {
      const response = await userRegister(
        postData.name,
        postData.username.toLowerCase(),
        postData.email,
        postData.password,
        postData.data_collection_consent || false,
        postData.terms_accepted,
        {
          intakeRef: postData.intakeRef ?? '',
          signupFormMs: Date.now() - formShownAt.current,
        },
      );
      if (response.error) {
        setRegisterError(response.error);
      } else if (response.data?.verification_email_status === 'unreachable') {
        // The account exists, but the verify gate they are about to land on
        // says "we sent a verification link" — and nothing was sent, because
        // that address can never receive mail. Without this they wait at a gate
        // they cannot pass, with no hint that the address is the problem
        // (Codex #832).
        toast.warning(
          'Account created, but that email address cannot receive mail. Change it to finish verifying.',
          { autoClose: false },
        );
      } else if (response.data?.verification_email_status === 'failed') {
        toast.warning(
          "Account created. We couldn't send the verification email just now — request another from the verify screen.",
        );
      } else {
        toast.success('Registration successful! Welcome to NVC!');
      }
    } catch {
      setRegisterError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
      finishSignIn();
    }
  };

  const handleGoogleRegister = async () => {
    setGoogleLoading(true);

    try {
      // Detect if we're in a mobile app environment
      const isMobileApp = Capacitor.isNativePlatform();

      // Add state parameter to indicate mobile app
      // Carry the stashed invite code through OAuth: localStorage can't reach the
      // backend callback, but the state param is echoed back by Google verbatim, so
      // it's the one channel that survives the round-trip. Parsed + format-validated
      // server-side; only used if this sign-in creates a NEW account.
      const referralCode = getStashedReferralCode();
      const clientType = isMobileApp ? 'mobile_app' : 'web_browser';
      // isGuest rides along as `src:guest_tour` so a guest-tour conversion via
      // Google is attributed server-side (new accounts only).
      const state = buildOAuthState(
        clientType,
        referralCode,
        useAuthStore.getState().isGuest,
        getStashedSignupSource(),
        getStashedSignupVariant(),
      );
      // The app's start carries a fresh device nonce the deep link must echo
      // back, so a link this device did not start cannot sign it in.
      const googleOAuthUrl = googleOAuthStartUrl(import.meta.env.VITE_API_URL, state, isMobileApp);

      if (isMobileApp) {
        // Open in system browser (Chrome Custom Tabs / ASWebAuthenticationSession)
        // WebViews are blocked by Google OAuth (403: disallowed_useragent)
        toast.info(
          'Opening Google registration... Please complete authentication and return to the app.',
          {
            autoClose: 5000,
          },
        );
        await Browser.open({ url: googleOAuthUrl });
        // Reset loading after browser opens — user will return to app later
        setGoogleLoading(false);
      } else {
        // Web: navigates away, so loading state doesn't matter
        window.location.href = googleOAuthUrl;
      }
    } catch {
      toast.error('Failed to open Google registration');
      setGoogleLoading(false);
    }
  };

  /**
   * Sign up / in with Apple (iOS only). Obtains a native identity token and
   * exchanges it via the auth store. The backend creates the account on first
   * use, or links Apple to an existing account matched by verified email.
   */
  const handleAppleRegister = async () => {
    setIsAppleLoading(true);
    postAuthPending.current = true;

    try {
      const result = await SignInWithApple.authorize({
        // clientId/redirectURI are required by the plugin's type but used only
        // by its web flow; the native iOS flow uses the app's entitlement.
        clientId: 'app.empathease.empathease',
        redirectURI: `${import.meta.env.VITE_APP_URL ?? 'https://empathease.app'}/auth/apple/callback`,
        scopes: 'name email',
      });

      const identityToken = result.response?.identityToken;
      if (!identityToken) {
        toast.error('Apple did not return an identity token. Please try again.');
        return;
      }

      const { error, ended } = await signInWithApple({
        identityToken,
        authorizationCode: result.response.authorizationCode,
        givenName: result.response.givenName,
        familyName: result.response.familyName,
        email: result.response.email,
      });

      if (error) {
        toast.error(error);
      } else if (!ended) {
        toast.success('Welcome to NVC!');
      }
    } catch (err) {
      // authorize() rejects on user cancellation as well as real failures.
      console.error('Apple sign-in failed:', err);
      toast.error('Apple sign-in was cancelled or could not be completed.');
    } finally {
      setIsAppleLoading(false);
      finishSignIn();
    }
  };

  return (
    <AuthShell
      title="Get"
      accent="Started"
      tagline="Create your account to begin learning NVC"
      footer={
        <p className="ee-auth-terms">
          By creating an account, you agree to our{' '}
          <AuthLegalLink to="/terms-of-use">Terms of Service</AuthLegalLink> and{' '}
          <AuthLegalLink to="/privacy">Privacy Policy</AuthLegalLink>
        </p>
      }
    >
      {/* "{Friend} invited you": shown when arriving via a referral link. */}
      {inviterName ? (
        <div data-testid="referral-invite-banner" className="ee-auth-invite">
          <span className="ee-auth-invite__name">{inviterName}</span> invited you to EmpathEase 🎉
        </div>
      ) : null}

      {/* Came from the circle-starting notice's Join (CIRCLE-STARTING-NOTICE.md §5). */}
      <PendingJoinNote join={pendingJoin} mode="register" />

      {/* Form-level registration error */}
      {registerError ? <AuthAlert>{registerError}</AuthAlert> : null}

      <form className="ee-auth-form" onSubmit={fireAndForget(handleSubmit(submitRegisterForm))}>
        {/*
          Honeypot. A value here means something filled a field no human can
          see, which is a script.

          Every attribute is defending against a DIFFERENT way a real person
          could trip it and be locked out of signing up with no explanation:

          - off-screen, not display:none — the first thing a scraper checks
          - aria-hidden + tabIndex={-1} — out of screen-reader and keyboard order
          - readOnly — keeps load-time autofill out. autoComplete="off"
            is not reliably honoured (Chrome ignores it; managers have
            fill-everything modes), so this is the attribute doing that job.
          - `intakeRef` — a name matching no autofill category. Not something
            obviously honeypot-shaped either, which a script would skip.

          On readOnly and bots, since this went back and forth: it costs the
          trap nothing. The field is 0x0 at zero opacity off-screen, so its
          bounding box is empty — Playwright and Selenium refuse to fill an
          element that is not visible, readOnly or not, and never could reach
          this one. What DOES fill it is a raw JSON post (our actual signup
          wave) or a direct `element.value = …` assignment, and neither
          consults readOnly at all.

          So the honest summary: readOnly narrows the way a REAL PERSON gets
          trapped, and does not narrow the way a bot does. It does not
          eliminate the human case either — a filler writing values directly
          still reaches it — which is why the rejection offers a route to a
          person.
        */}
        <input
          type="text"
          tabIndex={-1}
          aria-hidden="true"
          autoComplete="off"
          readOnly
          className="absolute left-[-9999px] h-0 w-0 opacity-0"
          {...register('intakeRef')}
        />

        <AuthField id="name" label="Full Name" icon={LuUser} error={errors?.name?.message}>
          <input
            type="text"
            id="name"
            {...register('name', {
              required: 'Full name is required',
              validate: (value) => value?.trim() !== '' || 'Full name cannot be empty',
            })}
            placeholder="Enter your full name"
          />
        </AuthField>

        <AuthField
          id="username"
          label="Username"
          icon={LuAtSign}
          hint="This will be your public profile URL: /u/username"
          error={errors?.username?.message}
        >
          <input
            type="text"
            id="username"
            {...register('username', {
              required: 'Username is required',
              minLength: { value: 3, message: 'Username must be at least 3 characters' },
              maxLength: { value: 30, message: 'Username cannot exceed 30 characters' },
              pattern: {
                value: /^[a-zA-Z0-9_]+$/,
                message: 'Username can only contain letters, numbers, and underscores',
              },
            })}
            placeholder="Choose a unique username"
          />
        </AuthField>

        <AuthField id="email" label="Email" icon={LuMail} error={errors?.email?.message}>
          <input
            type="email"
            id="email"
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i,
                message: 'Enter a valid email',
              },
            })}
            placeholder="Enter your email"
          />
        </AuthField>

        <AuthField
          id="password"
          label="Password"
          icon={LuLock}
          hint="Minimum 8 characters"
          error={errors?.password?.message}
          trailing={
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              className="ee-auth-field__toggle"
            >
              {showPassword ? <LuEyeOff aria-hidden="true" /> : <LuEye aria-hidden="true" />}
            </button>
          }
        >
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Password must be at least 8 characters' },
            })}
            placeholder="Create a strong password"
          />
        </AuthField>

        {/* Data Collection Consent - Optional */}
        <AuthCheck
          label="I consent to my interactions being used to improve AI features (optional)"
          hint="You can change this preference anytime in your profile settings."
        >
          <input
            type="checkbox"
            id="data_collection_consent"
            {...register('data_collection_consent')}
          />
        </AuthCheck>

        {/* Terms & Privacy Policy - Required. The links open a new tab, so the
            half-filled form stays. */}
        <AuthCheck
          label={
            <>
              I agree to the <AuthLegalLink to="/terms-of-use">Terms of Service</AuthLegalLink> and{' '}
              <AuthLegalLink to="/privacy">Privacy Policy</AuthLegalLink>
            </>
          }
          error={errors?.terms_accepted?.message || undefined}
        >
          <input
            type="checkbox"
            id="terms_accepted"
            {...register('terms_accepted', {
              required: 'You must accept the Terms of Service and Privacy Policy',
            })}
          />
        </AuthCheck>

        {/* Register Button */}
        <Button
          type="submit"
          fullWidth
          disabled={!isValid}
          loading={isLoading}
          leadingIcon={<LuUserPlus />}
        >
          {isLoading ? 'Creating your account...' : 'Create Account'}
        </Button>
      </form>

      <AuthDivider />

      {/* Google Registration */}
      <Button
        variant="secondary"
        fullWidth
        onClick={fireAndForget(handleGoogleRegister)}
        disabled={isLoading}
        loading={isGoogleLoading}
        leadingIcon={<GoogleMark />}
      >
        Continue with Google
      </Button>

      {/* Sign in with Apple: iOS only (Apple Review Guideline 4.8) */}
      {isApplePlatform ? (
        <Button
          variant="secondary"
          fullWidth
          className="ee-auth-apple"
          onClick={fireAndForget(handleAppleRegister)}
          disabled={isLoading || isGoogleLoading}
          loading={isAppleLoading}
          leadingIcon={<FaApple />}
        >
          Sign in with Apple
        </Button>
      ) : null}

      {/* Login Link */}
      <p className="ee-auth-switch">
        Already have an account?{' '}
        <Link className="ee-auth-link" to={withRedirect('/login', searchParams.get('redirect'))}>
          Sign In
        </Link>
      </p>

      {/* Guest tour: deliberately subtle. Catches visitors about to abandon the
          form (they were leaving anyway); the tour is the retention path, not a
          competitor to signup. */}
      <button
        type="button"
        onClick={() => {
          startGuest();
          void navigate('/app');
        }}
        className="ee-auth-quiet"
      >
        Just browsing? Preview the app first
      </button>
    </AuthShell>
  );
}

export default Register;
